import React from "react"
import { useRouter } from "next/router"

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Divider,
} from "@coval/ui"

import { DEBUG_INFO_OPEN_EVENT } from "@/lib/debugInfoEvents"
import { useCommandRegistry } from "@/contexts/CommandRegistryContext"
import { useKeyboardShortcuts } from "@/contexts/KeyboardShortcutsContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"

interface DebugInfoItem {
  label: string
  value: string
}

function formatLastPressedKey(lastPressedKey: string | null) {
  return lastPressedKey ?? "None"
}

function formatActiveEscapeLabel(activeEscapeLabel: string | null) {
  return activeEscapeLabel ?? "None"
}

function formatCommitSha(commitSha: string | undefined) {
  if (!commitSha) {
    return "Unavailable"
  }

  if (commitSha === "dev") {
    return "dev"
  }

  return commitSha.slice(0, 7)
}

export function DebugInfoPanel() {
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  const { registeredCommands } = useCommandRegistry()
  const { getActiveEscapeLabel, getShortcuts, lastPressedKey } =
    useKeyboardShortcuts()

  React.useEffect(() => {
    const handleOpen = () => {
      setOpen(true)
    }

    window.addEventListener(DEBUG_INFO_OPEN_EVENT, handleOpen)

    return () => {
      window.removeEventListener(DEBUG_INFO_OPEN_EVENT, handleOpen)
    }
  }, [])

  useEscapeShortcut({
    description: "Close debug info",
    enabled: open,
    handler: () => setOpen(false),
    priority: ESCAPE_SHORTCUT_PRIORITY.DIALOG,
  })

  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV ?? "development"
  const releaseVersion = process.env.NEXT_PUBLIC_RELEASE_VERSION
  const isProduction = vercelEnv === "production"
  const debugInfo = React.useMemo<DebugInfoItem[]>(() => {
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Unavailable"
    const activeShortcuts = getShortcuts()
    const visiblePageCommands = registeredCommands.filter(
      (command) => command.available !== false,
    )
    const items: DebugInfoItem[] = [
      {
        label: "Version",
        value: isProduction
          ? (releaseVersion ??
            formatCommitSha(process.env.NEXT_PUBLIC_COMMIT_SHA))
          : formatCommitSha(process.env.NEXT_PUBLIC_COMMIT_SHA),
      },
      {
        label: "Commit",
        value: formatCommitSha(process.env.NEXT_PUBLIC_COMMIT_SHA),
      },
      {
        label: "Environment",
        value: vercelEnv,
      },
      {
        label: "Route",
        value: router.pathname,
      },
      {
        label: "Browser timezone",
        value: timezone,
      },
      {
        label: "Active shortcuts",
        value: String(activeShortcuts.size),
      },
      {
        label: "Page commands",
        value: String(visiblePageCommands.length),
      },
      {
        label: "Active Escape action",
        value: formatActiveEscapeLabel(getActiveEscapeLabel()),
      },
      {
        label: "Last shortcut",
        value: formatLastPressedKey(lastPressedKey),
      },
    ]

    if (!isProduction) {
      items.push({
        label: "Current path",
        value: router.asPath,
      })
    }

    return items
  }, [
    getActiveEscapeLabel,
    getShortcuts,
    isProduction,
    lastPressedKey,
    registeredCommands,
    releaseVersion,
    router.asPath,
    router.pathname,
    vercelEnv,
  ])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        size="md"
        className="max-h-[min(80vh,640px)] overflow-hidden p-0"
      >
        <DialogHeader className="gap-1 px-5 pt-5">
          <DialogTitle>Debug Info</DialogTitle>
          <DialogDescription>
            Public diagnostics for this session and page.
          </DialogDescription>
        </DialogHeader>
        <Divider />
        <DialogBody className="px-5 py-4">
          <div className="grid gap-3">
            {debugInfo.map((item) => (
              <div
                key={item.label}
                className="flex items-start justify-between gap-4 rounded-md border border-coval-border bg-surface-1 px-3 py-2"
              >
                <dt className="text-sm text-coval-text-muted">{item.label}</dt>
                <dd className="text-right font-mono text-sm text-coval-text">
                  {item.value}
                </dd>
              </div>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
